import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { icons } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme } from '../../buttons';

export enum NotificationTheme {
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  SUSTAINABILITY = 'sustainability',
  CAMPAIGN = 'campaign',
  TRANSPARENT = 'transparent',
}

export enum NotificationLayout {
  INLINE = 'inline',
  EDGE_TO_EDGE = 'edge_to_edge',
  OVERLAY = 'overlay',
}

export interface NotificationI18n {
  closeAriaLabel: string;
}

/**
 * A component to display varied forms of notifications.
 *
 * Zeplin designs are available [here](https://zpl.io/adG00Wl)
 *
 * There is many options available, but in many cases you won't need most of these.
 *
 * @example
 * <fcom-notification
 *   [theme]="theme"
 *   [layout]="layout"
 *   [isClosable]="isClosable"
 *   [linkText]="linkText"
 *   [linkUrl]="linkUrl"
 *   [iconName]="iconName"
 *   [contentSpaceClass]="ContentSpaceClass.PX_SMALL_Y"
 *   (linkClick)="linkClick($event)"
 *   (closeClick)="closeClick($event)">
 *   {{text}}
 * </fcom-notification>
 */
@Component({
  selector: 'fcom-notification-renewed',
  templateUrl: './notification-renewed.component.html',
  styleUrls: ['./notification-renewed.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationRenewedComponent implements OnInit, OnChanges {
  public readonly NotificationTheme = NotificationTheme;
  public readonly NotificationLayout = NotificationLayout;

  /**
   * What color scheme to use for the component
   */
  @Input()
  theme: NotificationTheme = NotificationTheme.INFO;

  /**
   * Whether the component should be inline, full width or toast
   */
  @Input()
  layout: NotificationLayout = NotificationLayout.INLINE;

  /**
   * Whether to show the close icon or not
   */
  @Input()
  isClosable = false;

  @Input()
  title: string;

  /**
   * Whether to display link text wrapped (on the same line) or not (on a new line).
   */
  @Input()
  horizontal = true;

  /**
   * Reduces paddings, font size and icon size
   */
  @Input()
  compact = false;

  /**
   * The text to use for a link. If no URL is passed it will be rendered as a button. Otherwise, it
   * will be an A tag.
   */
  @Input()
  linkText: string;

  /**
   * The url to go to. If you wish to handle this behavior yourself you can listen to the linkClick
   * event and `event.preventDefault` to stop the browser changing the page.
   */
  @Input()
  linkUrl: string;

  /**
   * Whether the link should open in a new window or not. Only applicable when linkText + linkUrl
   * is provided.
   */
  @Input()
  linkOpenInNewWindow = false;

  /**
   * The text to use for a secondary link. If no URL is passed it will be rendered as a button. Otherwise, it
   * will be an A tag.
   */
  @Input()
  secondaryLinkText: string;

  /**
   * The url to go with secondary link. If you wish to handle this behavior yourself you can listen to the linkClick
   * event and `event.preventDefault` to stop the browser changing the page.
   */
  @Input()
  secondaryLinkUrl: string;

  /**
   * Whether the link should open in a new window or not. Only applicable when linkText + linkUrl
   * is provided.
   */
  @Input()
  secondaryLinkOpenInNewWindow = false;

  /**
   * An icon to override the default icon used. Note that each NotificationTheme has a different
   * default icon
   */
  @Input()
  iconName: string;

  @Input()
  iconCategory: keyof icons = 'svg-library';

  /**
   * Any i18n labels used by the component. In most cases you can use the default for the component
   */
  @Input()
  i18n: NotificationI18n = {
    closeAriaLabel: 'actions.close',
  };

  /**
   * When the button/a tag is clicked the parent component can listen and handle the event. Note
   * that the event is sent so that you can stopPropagation/preventDefault if needed.
   */
  @Output()
  linkClick = new EventEmitter<Event>();

  /**
   * When the secondary button/a tag is clicked the parent component can listen and handle the event. Note
   * that the event is sent so that you can stopPropagation/preventDefault if needed.
   */
  @Output()
  secondaryLinkClick = new EventEmitter<Event>();

  /**
   * The close button was clicked and the notification should be closed. The parent component
   * should either remove the notification from the dom or hide it in some way.
   */
  @Output()
  closeClick = new EventEmitter<Event>();

  displayedIcon: string;

  ngOnInit(): void {
    this.displayedIcon = this.iconName ?? this.getDefaultIconForTheme();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.theme && !this.iconName) || (changes.iconName && !changes.iconName.currentValue)) {
      this.displayedIcon = this.getDefaultIconForTheme();
    }

    if (changes.iconName && changes.iconName.currentValue) {
      this.displayedIcon = changes.iconName.currentValue;
    }
  }

  get notificationStyle(): Record<string, boolean> {
    return {
      'nordic-blue-50-bg nordic-blue-900-text': this.theme === NotificationTheme.INFO,
      'success-50-bg nordic-blue-900-text': this.theme === NotificationTheme.SUCCESS,
      'alert-50-bg nordic-blue-900-text': this.theme === NotificationTheme.WARNING,
      'error-50-bg nordic-blue-900-text': this.theme === NotificationTheme.ERROR,
      'birch-50-bg nordic-blue-900-text': this.theme === NotificationTheme.SUSTAINABILITY,
      'heather-700-bg white-text dark': this.theme === NotificationTheme.CAMPAIGN,
      'rounded-large': [NotificationLayout.INLINE, NotificationLayout.OVERLAY].includes(this.layout),
      shadow: this.layout === NotificationLayout.OVERLAY,
    };
  }

  private getDefaultIconForTheme():
    | 'alert-triangle'
    | 'checkmark-block'
    | 'sustainability'
    | 'discount-block'
    | 'info-block' {
    switch (this.theme) {
      case NotificationTheme.SUCCESS:
        return 'checkmark-block';
      case NotificationTheme.WARNING:
      case NotificationTheme.ERROR:
        return 'alert-triangle';
      case NotificationTheme.SUSTAINABILITY:
        return 'sustainability';
      case NotificationTheme.CAMPAIGN:
        return 'discount-block';
      case NotificationTheme.INFO:
      case NotificationTheme.TRANSPARENT:
      default:
        return 'info-block';
    }
  }

  protected readonly ButtonTheme = ButtonTheme;
}
